/* Мини-роутер на hash-навигации.
   Заменяет react-router: тому пакету на момент разработки не существует версии
   без открытых advisory, а мини-аппу нужны лишь 10 маршрутов, :params и query.
   API совместим с react-router, чтобы экраны не зависели от реализации. */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const RouterCtx = createContext(null);
const RouteCtx = createContext({ params: {} });

const readHash = () => {
  const raw = window.location.hash.replace(/^#/, "");
  /* Telegram передаёт свои параметры во фрагменте (#tgWebAppData=…&tgWebAppVersion=…).
     Это не наш маршрут — любой фрагмент, не начинающийся со «/», считаем корнем. */
  if (!raw.startsWith("/")) return { pathname: "/", search: "" };
  const [pathname, search = ""] = raw.split("?");
  return { pathname: pathname || "/", search: search ? `?${search}` : "" };
};

export function HashRouter({ children }) {
  const [loc, setLoc] = useState(readHash);
  // Глубина истории нужна, чтобы navigate(-1) с первого экрана не выкидывал из мини-аппа
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const onHash = () => setLoc(readHash());
    window.addEventListener("hashchange", onHash);
    // Фрагмент не переписываем: там лежат параметры запуска Telegram
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = useCallback(
    (to, opts = {}) => {
      if (typeof to === "number") {
        if (to < 0 && depth > 0) {
          setDepth((d) => Math.max(0, d + to));
          window.history.go(to);
        } else {
          setDepth(0);
          window.location.hash = "#/";
        }
        return;
      }
      const next = `#${to.startsWith("/") ? to : `/${to}`}`;
      if (opts.replace) {
        window.location.replace(next);
        setLoc(readHash());
      } else {
        setDepth((d) => d + 1);
        window.location.hash = next;
      }
    },
    [depth]
  );

  const value = useMemo(() => ({ ...loc, navigate }), [loc, navigate]);
  return <RouterCtx.Provider value={value}>{children}</RouterCtx.Provider>;
}

export const useLocation = () => {
  const ctx = useContext(RouterCtx);
  return { pathname: ctx.pathname, search: ctx.search };
};

export const useNavigate = () => useContext(RouterCtx).navigate;

export const useParams = () => useContext(RouteCtx).params;

export function useSearchParams() {
  const { search, navigate } = useContext(RouterCtx);
  const { pathname } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const setParams = useCallback(
    (next, opts = {}) => {
      const qs = next.toString();
      navigate(qs ? `${pathname}?${qs}` : pathname, opts);
    },
    [navigate, pathname]
  );
  return [params, setParams];
}

/** Сопоставляет шаблон «/deal/:id» с текущим путём */
function match(pattern, pathname) {
  if (pattern === "*") return { params: {} };
  const pa = pattern.split("/").filter(Boolean);
  const pb = pathname.split("/").filter(Boolean);
  if (pa.length !== pb.length) return null;
  const params = {};
  for (let i = 0; i < pa.length; i += 1) {
    if (pa[i].startsWith(":")) params[pa[i].slice(1)] = decodeURIComponent(pb[i]);
    else if (pa[i] !== pb[i]) return null;
  }
  return { params };
}

export function Route() {
  // Маркер конфигурации — рендерит его <Routes>
  return null;
}

export function Routes({ children }) {
  const { pathname } = useLocation();
  const routes = React.Children.toArray(children).filter(Boolean);

  // Точные маршруты имеют приоритет над «*»
  let fallback = null;
  for (const r of routes) {
    const { path, element } = r.props;
    if (path === "*") {
      fallback = element;
      continue;
    }
    const m = match(path, pathname);
    if (m) {
      return <RouteCtx.Provider value={m}>{element}</RouteCtx.Provider>;
    }
  }
  return fallback ? <RouteCtx.Provider value={{ params: {} }}>{fallback}</RouteCtx.Provider> : null;
}
